import React from "react";
import { Moon } from "lucide-react";

export const SLEEPWEAR_STYLES = [
  { v: "nightgown", label: "Nightgown only", desc: "Show nightgowns, not pajama sets." },
  { v: "pajama_set", label: "Pajama set only", desc: "Show retailer-verified pajamas, whether two-piece or one-piece/footed." },
  { v: "either", label: "Either", desc: "Show either style when the size and complete family match are verified." },
];
export const SLEEPWEAR_LABEL = Object.fromEntries(SLEEPWEAR_STYLES.map((s) => [s.v, s.label]));

const ageYears = (child) => {
  if (child?.birthday) {
    const d = new Date(child.birthday);
    if (!Number.isNaN(d.getTime())) {
      const t = new Date();
      const before = (t.getMonth() < d.getUTCMonth()) || (t.getMonth() === d.getUTCMonth() && t.getDate() < d.getUTCDate());
      return t.getFullYear() - d.getUTCFullYear() - (before ? 1 : 0);
    }
  }
  const n = parseFloat(child?.age);
  return Number.isFinite(n) ? n : null;
};

// Girls from toddler age (2+) onward; babies keep the existing sleeper logic and never see the field.
export const sleepwearEligible = (child) => {
  const t = (child?.type || "").toLowerCase();
  if (!t.includes("girl") || t.includes("baby")) return false;
  const age = ageYears(child);
  return age === null || age >= 2;
};

export function SleepwearStyleField({ value, onChange, migrated, idPrefix = "sleepwear-style" }) {
  const current = value || "either";
  return (
    <fieldset data-testid="wizard-sleepwear-style" className="border-0 p-0 m-0 min-w-0">
      <legend className="text-xs font-bold uppercase tracking-wider text-ink-muted flex items-center gap-1.5">
        <Moon size={12} className="text-brand" aria-hidden="true" /> Preferred sleepwear style
      </legend>
      <p id={`${idPrefix}-help`} className="text-xs text-ink-muted mt-1" data-testid="sleepwear-style-help">Choose what you would like us to find for her. You can change this anytime.</p>
      {migrated && (
        <p role="status" data-testid="sleepwear-style-migrated-note" className="mt-1.5 text-xs font-semibold text-gold-text bg-gold/10 border border-gold/30 rounded-xl px-3 py-2">
          We set this to Either — please confirm.
        </p>
      )}
      <div className="grid gap-2 mt-2">
        {SLEEPWEAR_STYLES.map((s) => {
          const id = `${idPrefix}-${s.v}`;
          const active = current === s.v;
          return (
            <label
              key={s.v}
              htmlFor={id}
              data-testid={`sleepwear-style-option-${s.v}`}
              className={`flex items-start gap-3 min-h-[44px] rounded-xl border-2 px-3 py-2.5 cursor-pointer transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-brand has-[:focus-visible]:ring-offset-1 ${active ? "border-brand bg-blush/50" : "border-line bg-white hover:border-brand/40"}`}
            >
              <input
                type="radio"
                id={id}
                name={`${idPrefix}-group`}
                value={s.v}
                checked={active}
                required
                onChange={() => onChange(s.v)}
                aria-describedby={`${idPrefix}-help ${id}-desc`}
                data-testid={`sleepwear-style-${s.v}`}
                className="mt-1 h-4 w-4 shrink-0 accent-[var(--brand,#b3325b)]"
              />
              <span className="min-w-0">
                <span className="block text-sm font-bold text-ink">{s.label}</span>
                <span id={`${id}-desc`} className="block text-xs text-ink-soft">{s.desc}</span>
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
